// ============================================================================
// WatchInviteModal - generate a single-use code for friends/family to spectate
// ============================================================================
// Server (POST /watch/createInvite) hands back an 8-char hex code valid for
// 1 hour. The recipient pastes it on the watchers page to follow this user's
// table. Cap is enforced server-side via WATCH_MAX_PER_HOST.
// ============================================================================
import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  Box, Text, Button, HStack, VStack, useClipboard, Spinner,
} from '@chakra-ui/react';
import { Eye, Copy, Check, RefreshCw } from 'lucide-react';
import gameApiService from '../services/gameApiService';
import { POKER_COLORS } from '../theme/designSystem';

interface WatchInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WatchInviteModal: React.FC<WatchInviteModalProps> = ({ isOpen, onClose }) => {
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [limits, setLimits] = useState<{ current?: number; max?: number }>({});
  const { onCopy, hasCopied } = useClipboard(code);

  const generate = async () => {
    setLoading(true);
    setError(null);
    const r = await gameApiService.createWatchInvite();
    setLoading(false);
    if (!r.success || !r.inviteCode) {
      setError(r.error || 'Could not generate code');
      return;
    }
    setCode(r.inviteCode);
    setExpiresAt(r.expiresAt || null);
    setLimits({ current: r.currentWatchers, max: r.maxWatchers });
  };

  const expiresLabel = expiresAt ? (() => {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return 'Expired';
    const mins = Math.round(ms / 60000);
    return mins >= 60 ? `${Math.round(mins / 60)}h` : `${mins}m`;
  })() : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay bg="rgba(0,0,0,0.7)" backdropFilter="blur(4px)" />
      <ModalContent bg="gray.900" color="white" border="1px solid" borderColor={`${POKER_COLORS.cyan}55`}>
        <ModalHeader>
          <HStack>
            <Eye size={18} color={POKER_COLORS.cyan} />
            <Text>Invite a Watcher</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody pb={6}>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.400">
              Share this code with a friend or family member. They paste it in
              their app to watch your table live. Each code is single-use and
              expires in 1 hour.
            </Text>

            {!code && (
              <Button colorScheme="cyan" leftIcon={<Eye size={14} />}
                onClick={generate} isLoading={loading}>
                Generate code
              </Button>
            )}

            {code && (
              <Box
                bg="rgba(6,182,212,0.08)" border="1px solid"
                borderColor={`${POKER_COLORS.cyan}55`} borderRadius="md" p={4}
              >
                <Text fontSize="xs" color="gray.500" letterSpacing="wide" mb={1}>
                  WATCH CODE
                </Text>
                <HStack>
                  <Text fontSize="2xl" fontWeight="bold" letterSpacing="3px"
                    color={POKER_COLORS.cyan} fontFamily="ui-monospace, monospace">
                    {code}
                  </Text>
                  <Button size="sm" variant="ghost" colorScheme="cyan"
                    leftIcon={hasCopied ? <Check size={14} /> : <Copy size={14} />}
                    onClick={onCopy}>
                    {hasCopied ? 'Copied' : 'Copy'}
                  </Button>
                </HStack>
                <HStack mt={2} fontSize="xs" color="gray.500" spacing={4}>
                  {expiresLabel && <Text>Expires in {expiresLabel}</Text>}
                  {limits.max !== undefined && (
                    <Text>Watchers: {limits.current ?? 0}/{limits.max}</Text>
                  )}
                </HStack>
              </Box>
            )}

            {code && (
              <Button size="sm" variant="outline" colorScheme="gray"
                leftIcon={<RefreshCw size={12} />}
                onClick={generate} isLoading={loading}>
                Generate another
              </Button>
            )}

            {error && (
              <Box bg="red.900" border="1px solid" borderColor="red.700"
                borderRadius="md" p={3}>
                <Text fontSize="sm" color="red.200">{error}</Text>
              </Box>
            )}

            {loading && !code && (
              <HStack justify="center"><Spinner size="sm" color={POKER_COLORS.cyan} /></HStack>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default WatchInviteModal;
